// Implements: F-VER-007, F-UIX-021, F-PRV-001
/**
 * Two versions of one mockup side by side, inside the Lienzo editor (owner report, round 3: "Comparar"
 * used to leave the editor for a page of code).
 *
 * Left the chosen version, right the current one, each in the application's one sandboxed frame
 * through the same frame route the preview uses (`SandboxedFrame`, `previewDocumentPath`). One device
 * size applies to both; each frame is drawn at that layout width and scaled down to fit its half, so
 * the document inside evaluates its media queries at the chosen width. The code differences (the
 * existing diff, a free read) are a secondary view, asked for with a toggle. The close button and
 * Escape go back to the normal preview. Escape is claimed in the capture phase, so the page's own
 * Escape (cancelling a paid turn in flight, F-UIX-017) never fires from the same press; an Escape
 * meant for a dialog or a menu open over the page stays theirs.
 *
 * The geometry reaches the CSS through `style.setProperty` (the CSP bans `style=""`).
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement, RefObject } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { SandboxedFrame } from "../../../shared/ui/atoms/SandboxedFrame";
import { previewDocumentPath } from "../../../shared/ui/frameDocumentPath";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import { VersionDiff } from "../components/VersionDiff";
import { useVersionDiff } from "../hooks/useVersionDiff";
import { useVersionTree } from "../hooks/useVersionTree";
import styles from "./VersionSideBySideContainer.module.css";

type Device = "desktop" | "tablet" | "mobile";
/** The preview's preset widths (the viewer's `VIEWPORT_PRESETS`), in CSS pixels. */
const DEVICE_WIDTHS: Record<Device, number> = { desktop: 1280, tablet: 768, mobile: 375 };
const OVERLAY = '[role="dialog"], [aria-modal="true"], [role="menu"], dialog[open]';

export interface VersionSideBySideContainerProps {
  readonly mockupId: string;
  /** The version compared with the current one. */
  readonly versionId: string;
  readonly onClose: () => void;
}

export function VersionSideBySideContainer({
  mockupId,
  versionId,
  onClose,
}: VersionSideBySideContainerProps): ReactElement {
  const { t } = useTranslation();
  const tree = useVersionTree(mockupId);
  const currentId = tree.status === "success" ? tree.data.currentVersionId : null;
  const [device, setDevice] = useState<Device>("desktop");
  const [code, setCode] = useState(false);
  const diff = useVersionDiff(mockupId, code && currentId !== null ? versionId : null, currentId ?? "");
  const heading = useRef<HTMLHeadingElement | null>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented || event.repeat) return;
      if (document.querySelector(OVERLAY) !== null) return;
      event.preventDefault();
      close.current();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  const ready = tree.status === "success" && currentId !== null;
  useEffect(() => {
    if (ready) heading.current?.focus();
  }, [ready]);

  if (tree.status === "error") {
    return (
      <section className={styles.compare} aria-label={t("editor.compare.label")}>
        <ErrorState error={tree.error} onRetry={tree.refetch} />
      </section>
    );
  }
  if (!ready) {
    return (
      <section className={styles.compare} aria-label={t("editor.compare.label")}>
        <LoadingState label={t("editor.compare.loading")} />
      </section>
    );
  }

  const numberOf = (id: string): number => tree.data.nodes.find((node) => node.id === id)?.sequence_number ?? 0;
  const from = numberOf(versionId);
  const to = numberOf(currentId);
  const width = DEVICE_WIDTHS[device];

  return (
    <section className={styles.compare} aria-label={t("editor.compare.label")}>
      <div className={styles.bar}>
        <h2 ref={heading} tabIndex={-1} className={styles.title}>
          {t("editor.compare.title", { from, to })}
        </h2>
        <SegmentedControl<Device>
          label={t("preview.viewport.group")}
          options={[
            { value: "desktop", label: t("preview.viewport.desktop"), icon: "monitor" },
            { value: "tablet", label: t("preview.viewport.tablet"), icon: "tablet" },
            { value: "mobile", label: t("preview.viewport.mobile"), icon: "phone" },
          ]}
          value={device}
          onChange={setDevice}
          disabled={code}
        />
        <span className={styles.spacer} />
        <Button
          variant="ghost"
          size="sm"
          pressed={code}
          onClick={() => {
            setCode((value) => !value);
          }}
        >
          <Icon name="code" size="sm" />
          {t("editor.compare.code")}
        </Button>
        <Button variant="secondary" size="sm" keyShortcuts="Escape" onClick={onClose}>
          <Icon name="close" size="sm" />
          {t("editor.compare.close")}
        </Button>
      </div>
      {code ? (
        <div className={styles.code}>
          {diff.isPending ? <LoadingState label={t("common.state.loading")} /> : null}
          {diff.isError ? (
            <ErrorState
              error={diff.error}
              onRetry={() => {
                void diff.refetch();
              }}
            />
          ) : null}
          {diff.isSuccess ? (
            <>
              <h3 className={styles.codeHeading}>{t("compare.heading", { from, to })}</h3>
              <VersionDiff pieces={diff.data.per_kind} />
            </>
          ) : null}
        </div>
      ) : (
        <div className={styles.panes}>
          <ComparePane
            label={t("editor.compare.chosen", { n: from })}
            frameTitle={t("editor.compare.frame", { n: from })}
            src={previewDocumentPath(mockupId, versionId)}
            width={width}
          />
          <ComparePane
            label={t("editor.compare.current", { n: to })}
            frameTitle={t("editor.compare.frame", { n: to })}
            src={previewDocumentPath(mockupId, currentId)}
            width={width}
          />
        </div>
      )}
    </section>
  );
}

interface ComparePaneProps {
  readonly label: string;
  readonly frameTitle: string;
  readonly src: ReturnType<typeof previewDocumentPath>;
  readonly width: number;
}

function ComparePane({ label, frameTitle, src, width }: ComparePaneProps): ReactElement {
  const pane = useRef<HTMLDivElement | null>(null);
  const space = usePaneWidth(pane);
  const scale = space === null ? 1 : Math.min(1, space / width);
  useLayoutEffect(() => {
    pane.current?.style.setProperty("--compare-width", `${String(width)}px`);
    pane.current?.style.setProperty("--compare-scale", String(scale));
  }, [width, scale]);
  return (
    <figure className={styles.pane}>
      <figcaption className={styles.label}>{label}</figcaption>
      <div ref={pane} className={styles.stage} data-testid="compare-pane">
        <SandboxedFrame key={src} className={styles.frame} title={frameTitle} src={src} />
      </div>
    </figure>
  );
}

/** The width the pane has, followed as it changes; `null` where nothing measures (no observer). */
function usePaneWidth(pane: RefObject<HTMLDivElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = pane.current;
    if (element === null || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured !== undefined && measured > 0) setWidth(measured);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [pane]);
  return width;
}
